import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { OAuth2Client, OAuthStatusTypes } from "@trumpf/ux.utils";
import authConfig from "./config";

const oauthClient = new OAuth2Client(authConfig);

const redirectToAuthCallbackUrl = () => {
    const { location } = window.top;
    const params = new URLSearchParams(location.search);
    const path = params.get("path");
    const cleanUrl = `${location.protocol}//${location.host}${location.pathname}?path=${path}`;
    if (window.top.location.href !== cleanUrl) {
        window.top.history.replaceState({}, document.title, cleanUrl);
    }
};

@customElement("demo-auth")
export class DemoAuth extends LitElement {
    @property({ type: Boolean }) isLoggedIn = oauthClient.getStatus() === OAuthStatusTypes.AUTHENTICATED;
    @property({ type: Boolean }) isPending = oauthClient.getStatus() === OAuthStatusTypes.PENDING;
    @property({ type: Date }) expiration = new Date(oauthClient.getTokenExpiration());
    unsubscribe: () => void;

    connectedCallback() {
        super.connectedCallback();
        const update = () => {
            const status = oauthClient.getStatus();
            this.isLoggedIn = status === OAuthStatusTypes.AUTHENTICATED;
            this.isPending = status === OAuthStatusTypes.PENDING;
            this.expiration = new Date(oauthClient.getTokenExpiration());
        };
        update();
        this.unsubscribe = oauthClient.subscribe(update);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.unsubscribe();
    }

    handleClick = () => {
        if (this.isLoggedIn) {
            this.handleLogout();
        } else {
            this.handleLogin();
        }
    };

    handleLogin = () => {
        this.isPending = true;
        oauthClient.login();
    };

    handleLogout = () => {
        this.isPending = true;
        oauthClient.logout();
    };

    handleDeleteToken = () => {
        oauthClient.deleteAccessToken();
    };

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        super.updated(changedProperties);
        if (changedProperties.has("isLoggedIn") || changedProperties.has("isPending")) {
            if (!this.isPending) {
                redirectToAuthCallbackUrl();
            }
        }
    }

    static styles = css`
        :host {
            display: flex;
            min-height: 100%;
            padding: 0;
            background: transparent;
            font-family: var(--ux-font);
        }

        .auth {
            top: 10px;
            right: 10px;
            width: auto;
            max-width: 220px;
            margin: 0;
            background: white;
            border-radius: 6px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            overflow: hidden;
            font-size: 12px;
            z-index: 1000;
        }

        .auth-header {
            padding: 0.4rem 0.75rem;
            background: #95c11f;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        .login-status {
            font-size: 0.8rem;
            font-weight: 500;
            color: white;
            margin: 0;
        }

        .auth-content {
            padding: 0.5rem 0.75rem;
        }

        .session-info {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }

        .user-meta {
            display: none;
        }

        .session-status {
            display: none;
        }

        .button-group {
            display: flex;
            gap: 0.4rem;
        }

        .ux-button {
            flex: 1;
            padding: 0.25rem 0.5rem;
            border-radius: 3px;
            font-size: 0.7rem;
            font-weight: 400;
            border: none;
            cursor: pointer;
            background: transparent;
            transition: background-color 0.2s ease;
        }

        .ux-button[variant="primary"] {
            background: var(--ux-color-primary);
            color: white;
        }

        .ux-button[variant="secondary"] {
            background: var(--ux-color-surface);
            color: var(--ux-color-text);
        }

        .expiration-info {
            display: flex;
            justify-content: center;
            font-size: 0.65rem;
            color: #666;
        }

        .time-display {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.25rem;
        }

        .time-label {
            font-size: 0.65rem;
            color: #666;
        }

        time {
            font-size: 0.65rem;
            color: #666;
        }

        .loading-state {
            display: flex;
            align-items: center;
            gap: 0.4rem;
            padding: 0.25rem 0;
        }

        .loading-text {
            font-size: 0.7rem;
            margin: 0;
        }

        @media (max-width: 480px) {
            .auth {
                top: 5px;
                right: 5px;
                border-radius: 4px;
            }

            .auth-header {
                padding: 0.3rem 0.5rem;
            }

            .auth-content {
                padding: 0.3rem 0.5rem;
            }
        }
    `;

    render() {
        return html`
            <div class="auth">
                <div class="auth-header">
                    <h2 class="login-status">${this.isLoggedIn ? "Secure Session" : "Authentication Required"}</h2>
                </div>

                <div class="auth-content">
                    ${this.isPending
                        ? html`
                              <div class="loading-state">
                                  <ux-loader size="small"></ux-loader>
                                  <p class="loading-text">Authenticating...</p>
                              </div>
                          `
                        : html`
                              ${this.isLoggedIn
                                  ? html`
                                        <div class="session-info">
                                            <div class="button-group">
                                                <button class="ux-button" variant="secondary" @click=${this.handleLogout}>
                                                    Sign Out
                                                </button>
                                                <button class="ux-button" variant="secondary" @click=${this.handleDeleteToken}>
                                                    Clear Token
                                                </button>
                                            </div>

                                            <div class="expiration-info">
                                                <div class="time-display">
                                                    <span class="time-label">Expires:</span>
                                                    <time datetime=${this.expiration.toISOString()}>
                                                        ${this.expiration.toLocaleDateString()}
                                                        ${this.expiration.toLocaleTimeString()}
                                                    </time>
                                                </div>
                                            </div>
                                        </div>
                                    `
                                  : html`
                                        <div class="auth-cta">
                                            <button class="ux-button" variant="primary" @click=${this.handleLogin}>
                                                Continue with MyTRUMPF
                                            </button>
                                        </div>
                                    `}
                          `}
                </div>
            </div>
        `;
    }
}

export default DemoAuth;
